import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Download } from "lucide-react";
import { useFinance, Modal, Field, inputCls, kicker, Loading, EmptyState, fmt, todayISO, downloadCsv } from "@/admin/finance/financeUtils";

export default function FinanceExpenses({ settings }) {
  const { data, loading, reload } = useFinance("/finance/expenses", []);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "Cleaning supplies", amount: "", vendor: "", payment_method: "Cash", date: todayISO(), notes: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!Number(f.amount)) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try { await api.post("/finance/expenses", { ...f, amount: Number(f.amount) }); toast.success("Expense added"); setOpen(false); setF({ ...f, amount: "", vendor: "", notes: "" }); reload(); }
    catch (e) { console.error(e); toast.error("Could not save"); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/finance/expenses/${id}`); reload(); } catch (e) { console.error(e); } };

  const total = (data || []).reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div data-testid="finance-expenses">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className={kicker}>Expenses · {fmt(total)} total</p>
        <div className="flex gap-2">
          <button onClick={() => downloadCsv("/finance/export/expenses", "ascendia-expenses.csv")} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-xs text-white hover:bg-white/5"><Download className="h-3.5 w-3.5" /> CSV</button>
          <button onClick={() => setOpen(true)} data-testid="expense-add" className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-black hover:bg-gold-soft"><Plus className="h-4 w-4" /> Add expense</button>
        </div>
      </div>
      {loading && <Loading />}
      {data && data.length === 0 && <EmptyState label="No expenses logged yet." />}
      {data && data.length > 0 && (
        <div className="ascendia-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-c border-b border-[color:var(--border-subtle)]"><th className="p-3">Date</th><th className="p-3">Category</th><th className="p-3">Vendor</th><th className="p-3 text-right">Amount</th><th className="p-3"></th></tr></thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.id} className="border-b border-[color:var(--border-subtle)]/40" data-testid="expense-row">
                  <td className="p-3 text-muted-c whitespace-nowrap">{r.date}</td>
                  <td className="p-3 text-white">{r.category}{r.job_id && <span className="ml-2 text-[10px] text-gold-soft">job-linked</span>}</td>
                  <td className="p-3 text-secondary">{r.vendor || "—"}</td>
                  <td className="p-3 text-right text-white tabular-nums">{fmt(r.amount)}</td>
                  <td className="p-3 text-right"><button onClick={() => del(r.id)} className="text-rose-300 hover:text-rose-200"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add expense" testid="expense-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount"><input type="number" autoFocus className={inputCls} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount" /></Field>
          <Field label="Date"><input type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Category"><select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="expense-category">{(settings?.expense_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
          <Field label="Method"><select className={inputCls} value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })}>{(settings?.payment_methods || []).map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
          <Field label="Vendor"><input className={inputCls} value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} /></Field>
          <Field label="Notes"><input className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        </div>
        <button onClick={save} disabled={busy} data-testid="expense-save" className="mt-4 w-full rounded-xl bg-gold py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">{busy ? "Saving…" : "Save expense"}</button>
      </Modal>
    </div>
  );
}
